import { Error } from '@mui/icons-material'
import { Alert, Box, CircularProgress, Tooltip } from '@mui/material'
import {
  GridColDef,
  GridSortModel,
  DataGrid,
  GridColumnHeaderParams,
} from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DefaultFetchDeviceFilters,
  DurationTestStatus,
  TransferState,
} from '@/api/definition'
import { useFetchDevicePage } from '@/api/devicePage'
import DeviceInfoButton from '@/components/DeviceInfoButton'
import type { Device } from '@/pages/Device/Device'
import types from '@/pages/Device/DeviceType'
import { IsOnline, Uid } from '@/pages/utils/formatter'
import { CustomToolbar, CustomToolbarProps } from '@/components/DataGridToolBar'
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions'
import { FilterDrawer } from '@/components/FilterDrawer'
import { useFilters } from '@/hooks/useFilters'
import { useDeviceTypeFilters } from '@/hooks/useTransalationForFilter'
import { useTableUrlState } from '@/hooks/useTableUrlState'

interface GridRowClickParams {
  row: Device
  id: string | number
  field?: string
  value?: unknown
  api?: unknown
  [key: string]: unknown
}

type Props = {
  autoRefresh?: boolean
  enableFilters?: boolean
  enableSearch?: boolean
}

const VerifyDeviceStatus = ({
  autoRefresh,
  enableFilters = true,
  enableSearch = true,
}: Props): React.JSX.Element => {
  const { t } = useTranslation(['di', 'app'])
  const { enqueueSnackbar } = useSnackbar()
  const { fetchDevicePage } = useFetchDevicePage()
  const { urlState, updateUrlState } = useTableUrlState()

  // Local state only for drawer
  const localFilterHooks = useFilters()

  // Data state
  const [deviceData, setDeviceData] = useState<Device[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [rowCount, setRowCount] = useState<number>(0)
  const abortController = useRef(new AbortController())

  // Local search state for immediate UI updates
  const [localSearchTerm, setLocalSearchTerm] = useState(urlState.searchTerm)
  const searchDebounceTimer = useRef<NodeJS.Timeout | undefined>(undefined)

  const filterConfig = useDeviceTypeFilters()

  const page = urlState.pageIndex
  const pageSize = urlState.pageSize
  const searchTerm = urlState.searchTerm
  const sortModel = urlState.sortModel
  const selectedFilters = urlState.filters

  // Device filters - same as used in fetchDeviceVerifyCount
  const deviceFilters = useMemo(
    () => ({
      ...DefaultFetchDeviceFilters,
      inService: true,
      durationTestStatus: [DurationTestStatus.DurationTestNotStarted],
      transferStatus: [TransferState.IN_PROGRESS],
    }),
    [],
  )

  // Row click handler
  const onRowClick = useCallback(
    (device: Device): boolean => {
      if (device.editable) {
        return true
      }
      enqueueSnackbar(t('device.rights.producerOnlyError'), {
        variant: 'error',
      })
      return false
    },
    [enqueueSnackbar, t],
  )

  const renderWrappedHeader = (params: GridColumnHeaderParams) => (
    <div
      style={{
        whiteSpace: 'normal',
        lineHeight: '1.5',
        fontWeight: 600,
        wordBreak: 'break-word',
        padding: '8px 0',
      }}
    >
      {params.colDef.headerName}
    </div>
  )

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'uid',
        headerName: t('di:device.uid'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <Uid value={params.value} />,
        width: 400,
        flex: 0,
        minWidth: 400,
        maxWidth: 400,
        resizable: false,
        sortable: true,
      },
      {
        field: 'serial',
        headerName: t('di:device.serialNr'),
        renderHeader: renderWrappedHeader,
        flex: 2,
        minWidth: 100,
      },
      {
        field: 'label',
        headerName: t('di:device.ownerLabel'),
        renderHeader: renderWrappedHeader,
        flex: 2,
        minWidth: 100,
      },
      {
        field: 'isOnline',
        headerName: t('di:device.isOnline'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <IsOnline value={params.value} />,
        flex: 2,
        minWidth: 80,
      },
      {
        field: 'type',
        headerName: t('di:device.type'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => {
          const deviceTypes = types as Record<string, string>
          const deviceType = deviceTypes[params.value] ?? params.value
          const deviceRoleName = params.row.deviceRoleName

          if (deviceRoleName) {
            return (
              <Tooltip title={deviceRoleName} placement="top" arrow>
                <Box
                  component="span"
                  sx={{ display: 'inline-block', width: '100%' }}
                >
                  {deviceType}
                </Box>
              </Tooltip>
            )
          }

          return deviceType
        },
        flex: 2,
        minWidth: 80,
      },
      {
        field: 'mobilizeTimedOut',
        headerName: t('di:device.status'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => {
          if (params.value) {
            return (
              <Error
                sx={{
                  color: '#f44336',
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  minHeight: '32px',
                }}
              />
            )
          }
          return (
            <CircularProgress
              size={20}
              sx={{
                color: '#ff9800',
                width: '32px',
                height: '32px',
                minWidth: '32px',
                minHeight: '32px',
              }}
            />
          )
        },
        flex: 2,
        minWidth: 80,
        sortable: false,
      },
      {
        field: 'action',
        headerName: '',
        renderHeader: renderWrappedHeader,
        flex: 2,
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        resizable: false,
        sortable: false,
        renderCell: (params) => <DeviceInfoButton params={params} />,
      },
    ],
    [t],
  )

  // Fetch device data function
  const fetchData = useCallback(async () => {
    abortController.current.abort()
    abortController.current = new AbortController()

    setLoading(true)
    setError(null)

    try {
      const sortBy =
        sortModel.length > 0
          ? [
              {
                id: sortModel[0].field,
                desc: sortModel[0].sort === 'desc',
              },
            ]
          : []

      const result = await fetchDevicePage(
        deviceFilters,
        {
          pageIndex: page,
          pageSize: pageSize,
          sortBy,
          globalFilter: searchTerm,
          selectedFilters: enableFilters ? selectedFilters : {},
        },
        false,
      )

      const transformedData = result.data?.length
        ? result.data.map((device) => ({
            ...device,
            id: device.uid,
          }))
        : []

      setDeviceData(transformedData)
      setRowCount(result.rowCount ?? 0)
    } catch (err) {
      if (
        err &&
        typeof err === 'object' &&
        'name' in err &&
        err.name !== 'AbortError'
      ) {
        console.error('Error fetching device data:', err)
        setError(
          'message' in err && typeof err.message === 'string'
            ? err.message
            : 'Failed to fetch device data',
        )
        setDeviceData([])
        setRowCount(0)
      }
    } finally {
      setLoading(false)
    }
  }, [
    page,
    pageSize,
    sortModel,
    searchTerm,
    enableFilters,
    selectedFilters,
    deviceFilters,
    fetchDevicePage,
  ])

  // Initial data fetch
  useEffect(() => {
    fetchData()
    return () => {
      abortController.current.abort()
    }
  }, [fetchData])

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(() => {
      if (!loading) {
        fetchData()
      }
    }, 30000)

    return () => clearInterval(interval)
  }, [autoRefresh, fetchData, loading])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }
    }
  }, [])

  const handlePaginationModelChange = useCallback(
    (params: { page: number; pageSize: number }) => {
      updateUrlState({
        pageIndex: params.page,
        pageSize: params.pageSize,
      })
    },
    [updateUrlState],
  )

  // Debounced search handler
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = event.target.value

      // Update local state immediately for responsive UI
      setLocalSearchTerm(newSearchTerm)

      // Clear existing timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }

      // Debounce the URL state update
      searchDebounceTimer.current = setTimeout(() => {
        updateUrlState({
          pageIndex: 0,
          searchTerm: newSearchTerm,
        })
      }, 300)
    },
    [updateUrlState],
  )

  const handleFilterChange = useCallback(
    (
      groupId: string,
      optionKey: string,
      optionLabel: string,
      isSelected: boolean,
    ) => {
      const newFilters = { ...selectedFilters }

      if (!newFilters[groupId]) {
        newFilters[groupId] = {}
      }

      if (isSelected) {
        newFilters[groupId][optionKey] = optionLabel
      } else {
        delete newFilters[groupId][optionKey]
        if (Object.keys(newFilters[groupId]).length === 0) {
          delete newFilters[groupId]
        }
      }

      updateUrlState({
        pageIndex: 0,
        filters: newFilters,
      })
    },
    [selectedFilters, updateUrlState],
  )

  const handleClearAllFilters = useCallback(() => {
    updateUrlState({
      pageIndex: 0,
      filters: {},
    })
  }, [updateUrlState])

  const handleRemoveFilter = useCallback(
    (groupId: string, optionKey: string) => {
      const currentLabel = selectedFilters[groupId]?.[optionKey] || ''
      handleFilterChange(groupId, optionKey, currentLabel, false)
    },
    [selectedFilters, handleFilterChange],
  )

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      updateUrlState({
        pageIndex: 0,
        sortModel: model,
      })
    },
    [updateUrlState],
  )

  const handleRowClick = useCallback(
    (params: GridRowClickParams) => {
      onRowClick(params.row)
    },
    [onRowClick],
  )

  const shouldShowCustomToolbar = enableFilters || enableSearch

  const activeFilterCount = enableFilters
    ? Object.values(selectedFilters).reduce(
        (count, group) => count + Object.keys(group).length,
        0,
      )
    : 0

  return (
    <>
      {error && (
        <Alert
          data-testid="verify-device-status-error-alert"
          severity="error"
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          borderRadius: '4px',
          overflow: 'auto',
          width: '100%',
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            lineHeight: '1.5',
          },
        }}
      >
        <DataGrid
          data-testid="device-grid-verify-status"
          rowHeight={50}
          rows={deviceData}
          columns={columns}
          autoHeight // DO NOT REMOVE - Even though 'autoHeight' is deprecated, the other alternatives do not fix the scrolling issue.
          disableVirtualization
          loading={loading}
          paginationMode="server"
          sortingMode="server"
          rowCount={rowCount}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onRowClick={handleRowClick}
          getRowId={(row) => row.uid}
          disableColumnFilter={true}
          disableColumnMenu={true}
          disableDensitySelector={true}
          disableColumnSelector={true}
          disableRowSelectionOnClick={true}
          slots={
            shouldShowCustomToolbar ? { toolbar: CustomToolbar } : undefined
          }
          slotProps={{
            toolbar: shouldShowCustomToolbar
              ? ({
                  onFilterClick: enableFilters
                    ? localFilterHooks.openDrawer
                    : undefined,
                  activeFilterCount: activeFilterCount,
                  enableFilters: enableFilters,
                  enableSearch: enableSearch,
                  selectedFilters: selectedFilters,
                  filterConfig: filterConfig,
                  onRemoveFilter: handleRemoveFilter,
                  searchValue: localSearchTerm,
                  onSearchChange: handleSearchChange,
                } as CustomToolbarProps)
              : undefined,
            pagination: {
              showFirstButton: true,
              showLastButton: true,
              ActionsComponent: TablePaginationActions,
            },
          }}
          localeText={{
            noRowsLabel: 'No devices found requiring verification',
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>

      {enableFilters && filterConfig && (
        <FilterDrawer
          open={localFilterHooks.isDrawerOpen}
          onClose={localFilterHooks.closeDrawer}
          filterConfig={filterConfig}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAllFilters}
        />
      )}
    </>
  )
}

export default VerifyDeviceStatus
